import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/_core/hooks/useAuth";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { TabScaffold } from "@/components/tabs/TabScaffold";
import {
  initialWeekTargetFromUrl,
  syncWeekUrlParam,
  weekOffsetForTarget,
} from "@/lib/weekParam";
import { weekKey, weekStartUTC, dayLabel } from "@shared/workweek";
import { YourWeekSections } from "@/components/yourweek/YourWeekSections";

/**
 * YOUR WEEK — Phase 2A / Stage A1.
 *
 * The owner's private view of their own working week. Read-only, derived at
 * read time, persisted nowhere. It holds NO disclosure control: the only link
 * out is a navigation affordance to the existing weekly receipt surface.
 *
 * Section order is FROZEN by the accepted design (D7) and rendered in
 * `YourWeekSections`.
 */
export default function YourWeek() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const { isAuthenticated } = useAuth();
  const ws = useActiveWorkspace();

  const prefs = trpc.preferences.me.useQuery(undefined, { enabled: isAuthenticated });
  const weekStartDay = prefs.data?.weekStartDay ?? 0;

  // Anchor `now` inside the component, not at module scope: a long-lived tab
  // must not keep calling a past week "this week".
  const now = useMemo(() => new Date(), []);
  const initialTarget = useMemo(() => initialWeekTargetFromUrl(), []);
  const [weekOffset, setWeekOffset] = useState(0);

  // Exact decode of a `?week=` deep link, ONCE, as soon as the preference is
  // known — the Sunday-based helper drifts by a week when weekStartDay > 0.
  //
  // The ref guard matters: without it the effect re-runs whenever the
  // preferences query object re-identifies and RESETS the week the owner just
  // navigated to. It also must not fight the URL sync below, which rewrites
  // `?week=` on every week change.
  const decodedRef = useRef(false);
  useEffect(() => {
    if (decodedRef.current || !prefs.data) return;
    decodedRef.current = true;
    const decoded = weekOffsetForTarget(initialTarget, weekStartDay, now);
    if (decoded !== 0) setWeekOffset(decoded);
  }, [prefs.data, initialTarget, weekStartDay, now]);

  const weekStartKey = useMemo(() => {
    const base = weekStartUTC(now, weekStartDay);
    base.setUTCDate(base.getUTCDate() + weekOffset * 7);
    return weekKey(base, weekStartDay);
  }, [now, weekStartDay, weekOffset]);

  // Keep ?week= in step with the viewed week so back/refresh/copy-link land on
  // the same week. Effect, never render — matching Team/TeamReport.
  useEffect(() => {
    syncWeekUrlParam(weekStartKey);
  }, [weekStartKey]);

  // D1 — "All my work" is the default. null = every workspace.
  const [filterTeamId, setFilterTeamId] = useState<number | null>(null);

  const view = trpc.yourWeek.forWeek.useQuery(
    { weekStartKey, workspaceTeamId: filterTeamId },
    { enabled: isAuthenticated && prefs.data !== undefined },
  );

  const isCurrentWeek = weekOffset === 0;

  const weekNav = (
    <div className="flex items-center gap-2 text-sm">
      <button
        type="button"
        onClick={() => setWeekOffset(o => o - 1)}
        aria-label={isAr ? "الأسبوع السابق" : "Previous week"}
        className="grid h-9 w-9 place-items-center rounded-full border border-border/60 hover:bg-accent/50"
      >
        {isAr ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
      <span className="tabular-nums text-muted-foreground">
        {new Intl.DateTimeFormat(isAr ? "ar-u-nu-latn" : "en", {
          day: "numeric",
          month: "short",
          timeZone: "UTC",
        }).format(new Date(`${weekStartKey}T00:00:00.000Z`))}
      </span>
      <button
        type="button"
        disabled={isCurrentWeek}
        onClick={() => setWeekOffset(o => Math.min(0, o + 1))}
        aria-label={isAr ? "الأسبوع التالي" : "Next week"}
        className="grid h-9 w-9 place-items-center rounded-full border border-border/60 hover:bg-accent/50 disabled:opacity-40"
      >
        {isAr ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {!isCurrentWeek ? (
        <button
          type="button"
          onClick={() => setWeekOffset(0)}
          className="rounded-full border border-border/60 px-3 py-1 text-xs hover:bg-accent/50"
        >
          {isAr ? "هذا الأسبوع" : "This week"}
        </button>
      ) : null}
    </div>
  );

  return (
    <TabScaffold
      title={isAr ? "أسبوعك" : "Your Week"}
      subtitle={weekNav}
      headerRight={
        <span
          data-testid="yourweek-private-label"
          className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground"
        >
          <Lock className="h-3 w-3" />
          {isAr ? "مرئية لك وحدك" : "Visible only to you"}
        </span>
      }
      state={view}
      isEmpty={d => d.isEmpty}
      emptyTitle={isAr ? "لا توجد التقاطات لهذا الأسبوع" : "Nothing captured for this week"}
      emptyHint={
        isAr
          ? "تمتلئ صفحة أسبوعك كلما التقطت. مرئية لك وحدك."
          : "Your Week fills in as you capture. Visible only to you."
      }
    >
      {data => (
        <YourWeekSections
          data={data}
          isAr={isAr}
          weekStartKey={weekStartKey}
          filterTeamId={filterTeamId}
          onFilterChange={setFilterTeamId}
          workspaces={ws.workspaces.map(w => ({
            id: w.team.id,
            name: w.team.name,
            isPersonal: w.team.isPersonal,
          }))}
          dayName={(dateKey: string) =>
            dayLabel(new Date(`${dateKey}T00:00:00.000Z`).getUTCDay(), isAr ? "ar" : "en")
          }
          ReceiptLink={({ children }: { children: React.ReactNode }) => (
            <Link href={`/drafts?week=${weekStartKey}`} data-testid="yourweek-receipt-link">
              {children}
            </Link>
          )}
        />
      )}
    </TabScaffold>
  );
}
